/**
 * Pseudonyme Widget-Analytics — sendet erst NACH Opt-in.
 *
 * Zeitmessung ohne Pre-Consent-Storage (TDDDG-sauber): performance.now()
 * ist relativ zum Navigationsstart, Routenwechsel merken wir uns in einer
 * Modul-Variablen (SPA = ein JS-Kontext). Erst die Zustimmung erzeugt die
 * zufällige session_id in sessionStorage.
 *
 * Bewusst NICHT erhoben: IP, User-Agent, Referrer-Pfade (nur Origin).
 */

import { apiFetch } from '@sizilianer/core/apiFetch';
import { currentGuestLanguage } from '@/i18n';
import { CONSENT_VERSION, hasChatbotConsent } from './consent';

/** Muss zu CLIENT_EVENT_TYPES (api/_lib/chatbot.ts) und zum DB-CHECK aus
 *  0076 passen — drei Stellen, eine Wahrheit. */
export type ChatbotEventType =
  | 'consent_given'
  | 'widget_loaded'
  | 'widget_opened'
  | 'first_interaction'
  | 'message_sent'
  | 'lead_submitted'
  | 'page_view';

const SESSION_KEY = 'sizilianer-chatbot-session';
const FIRST_INTERACTION_KEY = 'sizilianer-chatbot-fi';

let lastRouteChangeAt = 0;

/** Vom Launcher bei jedem Pathname-Wechsel aufgerufen. */
export function noteRouteChange(): void {
  lastRouteChangeAt = performance.now();
}

function getOrCreateSessionId(): string | null {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function safeReferrerOrigin(): string | null {
  try {
    if (!document.referrer) return null;
    const origin = new URL(document.referrer).origin;
    return origin === window.location.origin ? null : origin.slice(0, 120);
  } catch {
    return null;
  }
}

/** first_interaction einmal pro Browser-Session (Tab), dedupliziert. */
export function trackFirstInteractionOnce(): void {
  try {
    if (sessionStorage.getItem(FIRST_INTERACTION_KEY)) return;
    sessionStorage.setItem(FIRST_INTERACTION_KEY, '1');
  } catch {
    // ohne Storage: lieber gar nicht senden als doppelt
    return;
  }
  trackChatbotEvent('first_interaction');
}

export function trackChatbotEvent(type: ChatbotEventType): void {
  if (!hasChatbotConsent()) return;
  const sessionId = getOrCreateSessionId();
  if (!sessionId) return;

  const now = performance.now();
  const body = JSON.stringify({
    event_type: type,
    session_id: sessionId,
    path: window.location.pathname.slice(0, 200),
    time_on_site_ms: Math.min(Math.round(now), 86_400_000),
    time_on_page_ms: Math.min(
      Math.max(Math.round(now - lastRouteChangeAt), 0),
      86_400_000,
    ),
    language: currentGuestLanguage(),
    device_class: window.matchMedia('(max-width: 1023px)').matches
      ? 'mobile'
      : 'desktop',
    referrer_origin: safeReferrerOrigin(),
    consent_version: CONSENT_VERSION,
  });

  try {
    const blob = new Blob([body], { type: 'application/json' });
    if (navigator.sendBeacon?.('/api/chatbot/event', blob)) return;
  } catch {
    // fällt auf fetch zurück
  }
  void apiFetch('/api/chatbot/event', {
    method: 'POST',
    body,
    keepalive: true,
    headers: { 'content-type': 'application/json' },
  }).catch(() => {});
}
